import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Typography, Button, Box, Card, CardContent,
    Grid, Alert, Chip, Avatar, CircularProgress, Dialog, IconButton, Zoom
} from '@mui/material';
import { Restaurant, Fastfood, ShoppingCart, Close as CloseIcon } from '@mui/icons-material';
import NavbarMain from '../components/NavbarMain';
import { dietAPI } from '../services/api';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const DietsPage = () => {
    const navigate = useNavigate();
    const [diets, setDiets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDiet, setSelectedDiet] = useState(null);

    useEffect(() => {
        const fetchDiets = async () => {
            try {
                setLoading(true);
                const data = await dietAPI.getAll();
                setDiets(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('❌ Error:', err);
                setError('Грешка при вчитување на диетите.');
            } finally {
                setLoading(false);
            }
        };
        fetchDiets();
    }, []);

    const handleOrderClick = (diet, e) => {
        if (e) e.stopPropagation();
        navigate('/login');
    };

    if (loading) {
        return (
            <>
                <NavbarMain />
                <Box sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2
                }}>
                    <CircularProgress sx={{ color: '#ff7eb9' }} />
                    <Typography variant="h6" sx={{ color: '#ff7eb9' }}>Вчитување...</Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <SEO
                title="Диети и Исхрана - BodyLab by Anamarija"
                description="Персонализирани диетални планови и совети за здрава исхрана. Постигни ја идеалната тежина со BodyLab диетите за жени."
                url="/diets"
            />

            <NavbarMain />

            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)',
                pt: { xs: 12, md: 15 },
                pb: 10
            }}>
                <Container maxWidth="lg">

                    {/* ===== HEADER ===== */}
                    <Box textAlign="center" mb={8}>
                        <Box sx={{
                            width: 80, height: 80, borderRadius: '50%',
                            mx: 'auto', mb: 3,
                            background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 8px 32px rgba(255,126,185,0.35)',
                            fontSize: '2.2rem'
                        }}>
                            🥗
                        </Box>
                        <Typography variant="h1" sx={{
                            fontWeight: 900, mb: 2,
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '2rem', md: '3.5rem' }
                        }}>
                            Диететски Планови
                        </Typography>
                        <Typography variant="h2" sx={{
                            color: '#888', fontWeight: 400,
                            maxWidth: 600, mx: 'auto', mb: 3,
                            fontSize: { xs: '1rem', md: '1.25rem' }
                        }}>
                            Здравствени диетски планови прилагодени за вашите фитнес цели
                        </Typography>
                        <Box sx={{ width: 60, height: 4, borderRadius: 2, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)', mx: 'auto' }} />
                        <Chip label={`${diets.length} диети достапни`} sx={{ mt: 3, bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9', fontWeight: 'bold', border: '1px solid rgba(255,126,185,0.3)' }} />
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }}>{error}</Alert>}

                    {diets.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 12, border: '2px dashed rgba(255,126,185,0.3)', borderRadius: '20px', backgroundColor: 'rgba(255,126,185,0.03)' }}>
                            <Fastfood sx={{ fontSize: 80, color: '#ff7eb9', mb: 3 }} />
                            <Typography variant="h4" sx={{ color: '#ff7eb9', fontWeight: 'bold', mb: 2 }}>Наскоро!</Typography>
                            <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', borderRadius: '12px', px: 4, py: 1.5, fontWeight: 'bold' }}>Најави се</Button>
                        </Box>
                    ) : (
                        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                            {diets.map((diet) => (
                                <Grid item xs={12} sm={6} md={4} key={diet.id} sx={{ display: 'flex' }}>
                                    <Card
                                        onClick={() => setSelectedDiet(diet)}
                                        sx={{
                                            width: '100%',
                                            height: '100%', // ОВА ГИ ПРАВИ ИСТА ВИСИНА
                                            display: 'flex', flexDirection: 'column',
                                            borderRadius: '20px', backgroundColor: '#ffffff',
                                            cursor: 'pointer',
                                            border: '1px solid rgba(255,126,185,0.15)',
                                            boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 16px 48px rgba(255,126,185,0.2)' }
                                        }}
                                    >
                                        <Box sx={{ height: 5, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                        <CardContent sx={{ p: { xs: 3, md: 4 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box mb={2}>
                                                <Chip label="Диета" size="small" sx={{ bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9', fontWeight: 'bold' }} />
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                <Avatar sx={{ background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)', width: 48, height: 48 }}><Restaurant /></Avatar>
                                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', fontSize: '1.3rem' }}>{diet.name}</Typography>
                                            </Box>

                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: '#666',
                                                    mb: 3,
                                                    whiteSpace: 'pre-wrap',  // ✅ нови редови + празни редови
                                                    wordBreak: 'break-word'  // ✅ да не крши layout со долги зборови/линкови
                                                }}
                                            >
                                                {diet.description || ''}
                                            </Typography>

                                            <Box sx={{ mt: 'auto' }}>
                                                <Box sx={{ textAlign: 'center', py: 2, mb: 2, borderRadius: '12px', background: 'rgba(255,126,185,0.06)' }}>
                                                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#ff7eb9' }}>{diet.price} ден</Typography>
                                                    <Typography variant="caption" sx={{ color: '#888' }}>месечно</Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    startIcon={<ShoppingCart />}
                                                    onClick={(e) => handleOrderClick(diet, e)}
                                                    sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', color: 'white', py: 1.5, borderRadius: '12px', fontWeight: 'bold' }}
                                                >
                                                    Нарачај сега
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Container>
            </Box>

            {/* ===== FULL SCREEN MODAL ЗА ДИЕТИ ===== */}
            <Dialog
                fullScreen
                open={!!selectedDiet}
                onClose={() => setSelectedDiet(null)}
                TransitionComponent={Zoom}
            >
                <Box sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    pt: { xs: 25, md: 30 } // Твојата препорачана вредност за Navbar-от
                }}>

                    {selectedDiet && (
                        <Card sx={{
                            maxWidth: 800,
                            width: '100%',
                            borderRadius: '30px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                            overflow: 'visible',
                            position: 'relative' // Клучно за позицијата на иксот
                        }}>
                            {/* КОПЧЕ ЗА ЗАТВОРАЊЕ - Горе десно во картичката */}
                            <IconButton
                                onClick={() => setSelectedDiet(null)}
                                sx={{
                                    position: 'absolute',
                                    top: 15,
                                    right: 15,
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    color: '#ff7eb9',
                                    '&:hover': { bgcolor: '#ff7eb9', color: 'white' },
                                    zIndex: 10,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            >
                                <CloseIcon fontSize="medium" />
                            </IconButton>

                            <Box sx={{ height: 10, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)', borderRadius: '30px 30px 0 0' }} />

                            <CardContent sx={{ p: { xs: 4, md: 8 } }}>
                                <Box display="flex" alignItems="center" gap={3} mb={4}>
                                    <Avatar sx={{ background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)', width: 64, height: 64 }}>
                                        <Restaurant fontSize="large" />
                                    </Avatar>
                                    <Box sx={{ pr: 5 }}> {/* pr: 5 за да не го преклопи иксот на мобилен */}
                                        <Chip label="Персонализиран план" sx={{ mb: 1, bgcolor: '#ff7eb9', color: 'white', fontWeight: 'bold' }} />
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: '1.1rem',
                                                lineHeight: 1.8,
                                                color: '#444',
                                                mb: 5,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {selectedDiet.description || ''}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#444', mb: 5 }}>
                                    {selectedDiet.description}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3, pt: 4, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                    <Box>
                                        <Typography variant="h3" sx={{ fontWeight: 900, color: '#ff7eb9' }}>{selectedDiet.price} ден</Typography>
                                        <Typography variant="body2" color="text.secondary">План за 30 дена</Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<ShoppingCart />}
                                        onClick={(e) => handleOrderClick(selectedDiet, e)}
                                        sx={{ px: 6, py: 2, borderRadius: '15px', background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', fontWeight: 'bold', fontSize: '1.1rem' }}
                                    >
                                        Нарачај веднаш
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Dialog>

            <Footer />
        </>
    );
};

export default DietsPage;